/**
 * @fileoverview Checkout module for checkout flow integration.
 */

import { postToHost, onMessage } from "../../core/messenger";
import {
  sendRequest,
  handleResponse,
  generateRequestId,
} from "../../core/requests";
import { createSubscription } from "../../core/subscriptions";
import type { BaseMessage, CheckoutResponseMessage } from "../../core/types";
import { validateCheckout, logValidationErrors } from "../../core/validation";
import { CHECKOUT_EVENTS } from "./events";
import type {
  AddonInfo,
  CheckoutModule,
  CheckoutCreateConfig,
  CheckoutResultCallback,
  CheckoutItem,
  GetAddonsResult,
} from "./types";

export type {
  AddonInfo,
  CheckoutModule,
  CheckoutCreateConfig,
  CheckoutResult,
  CheckoutResultCallback,
  CheckoutItem,
  GetAddonsResult,
} from "./types";

/** Response message for getAddons */
interface GetAddonsResponseMessage extends BaseMessage {
  payload: {
    success: boolean;
    addons?: AddonInfo[];
    error?: { code: string; message: string };
  };
}

/**
 * Create the checkout module.
 */
export function createCheckoutModule(): CheckoutModule {
  const resultSubscription = createSubscription<CheckoutResultCallback>();

  // Track listener unsubscribe functions for cleanup
  const unsubscribers: Array<() => void> = [];

  // Listen for checkout responses
  unsubscribers.push(
    onMessage<CheckoutResponseMessage>(CHECKOUT_EVENTS.RESPONSE, (message) => {
      resultSubscription.notify({
        success: message.payload.success,
        order_id: message.payload.order_id,
        status: message.payload.status,
        error: message.payload.error,
        context: message.payload.context,
      });
    }),
  );

  // Listen for getAddons responses
  unsubscribers.push(
    onMessage<GetAddonsResponseMessage>(
      CHECKOUT_EVENTS.GET_ADDONS_RESPONSE,
      (message) => {
        if (message.requestId) {
          handleResponse<GetAddonsResult>(message.requestId, {
            success: message.payload.success,
            addons: message.payload.addons,
            error: message.payload.error,
          });
        }
      },
    ),
  );

  return {
    create(
      input: CheckoutItem | CheckoutItem[],
      config?: CheckoutCreateConfig,
    ): void {
      const items = Array.isArray(input) ? input : [input];

      // Validate input before sending
      const validation = validateCheckout({ items });
      if (!validation.valid) {
        logValidationErrors(CHECKOUT_EVENTS.CREATE, validation.errors);
        throw new Error(validation.errors[0]);
      }

      // Note: Using "*" as targetOrigin because the SDK runs in third-party iframes
      // and doesn't know the host's origin at build time. Origin validation is
      // performed by the host when receiving messages via its trusted origin list.
      postToHost(
        CHECKOUT_EVENTS.CREATE,
        {
          items: items.map((i) => ({
            type: i.type,
            slug: i.slug,
            quantity: i.quantity ?? 1,
          })),
          ...(config?.context !== undefined && { context: config.context }),
        },
        "*",
        generateRequestId(),
      );
    },

    onResult(callback: CheckoutResultCallback): () => void {
      return resultSubscription.subscribe(callback);
    },

    async getAddons(): Promise<GetAddonsResult> {
      try {
        return await sendRequest<GetAddonsResult>(
          CHECKOUT_EVENTS.GET_ADDONS,
          {},
          30000,
        );
      } catch (error) {
        return {
          success: false,
          error: {
            code: "REQUEST_FAILED",
            message: error instanceof Error ? error.message : "Unknown error",
          },
        };
      }
    },

    resetCache(): void {
      postToHost(CHECKOUT_EVENTS.RESET_CACHE, {}, "*");
    },

    destroy(): void {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      unsubscribers.length = 0;
      resultSubscription.clear();
    },
  };
}
